/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useHistory, useParams } from 'react-router-dom';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { callApi } from 'api/helpers/api.helpers';
import { draftActions, initialDraftState, submissionUpdateState, reducer } from '../draft/draftReducer';
import draftApi from '../../api/draft.api';
import { getUserReference } from '../../shared/submission/submissionPropertyAccessor';
import useProcedure from '../../procedure/useProcedure';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { stopApplicationLoading } from '../../redux/features/application/application.slice';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { selectVersion } from '../../redux/features/configuration/configuration.slice';
import { getSearchPageByProcedure, isLockError } from '../../shared/utils/objectUtils';
import { RoutePath } from '../../shared/enums/RoutePath';
import { showAlertSuccess } from '../../redux/features/alerts/alerts.slice';
import { isConfigurationVersionCorrect, sanitizeSubmission } from '../../shared/utils/submissionUtils';
import { handleBusinessRuleValidations, postProcess } from '../../shared/configuration/businessRules';
import { createMessage } from '../../redux/features/alerts/alerts.helper';
import { useFeesConfiguration } from '../../redux/features/configuration/useFeesConfiguration';

export default function useSubmission() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    submissionId,
    initialSubmissionProcedure, // used for Subsequent Actions
  } = useParams();
  const procedure = useProcedure();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [draftState, dispatchDraftState] = useReducer(reducer, initialDraftState);
  const [submission, setSubmission] = useState(null);
  const [hasLockError] = useState(null);
  const hasLockErrorRef = useRef(hasLockError);
  const [errors, setErrors] = useState([]);
  const [extraErrors, setExtraErrors] = useState({});
  const submissionRef = useRef(submission);
  const errorsRef = useRef(errors);
  const userReferenceRef = useRef(getUserReference(submission));
  const isEditRoute = history.location.pathname.endsWith('edit');
  const isViewRoute = history.location.pathname.endsWith('view');

  const isNewRoute = !isEditRoute && !isViewRoute;
  const [workflowValidations, setWorkflowValidations] = useState({ sectionValidations: {}, isValid: false });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const configurationVersion = useSelector(selectVersion);

  const { resource: validationSchema } = useConfiguration(
    ConfigResourceProps.SUBMISSION_VALIDATION_SCHEMA,
    procedure.id,
  );
  const submissionUiSchema = useConfiguration(ConfigResourceProps.SUBMISSION_UI_SCHEMA, procedure.id);

  // preload other configurations for later usage
  useConfiguration(ConfigResourceProps.ATTACHMENT_CONFIGURATION, procedure.id);
  useConfiguration(ConfigResourceProps.PARTIES_CONFIGURATION);
  useConfiguration(ConfigResourceProps.ADDRESSBOOK_UI_SCHEMA);
  useConfiguration(ConfigResourceProps.ADDRESSBOOK_VALIDATION_SCHEMA);
  useFeesConfiguration();

  const [uiSchema, setUiSchema] = useState(undefined);

  useEffect(() => {
    if (submissionUiSchema.ready) setUiSchema(submissionUiSchema.resource);
  }, [submissionUiSchema.ready]);

  useEffect(() => {
    submissionRef.current = submission;
  }, [submission, submissionRef]);

  const setErrorsWrapper = (error) => {
    setErrors(error);
    errorsRef.current = error;
  };

  const isVersionCorrect = isConfigurationVersionCorrect(submission, configurationVersion);

  const loadDraft = useCallback(async () => {
    if (submissionId) {
      let existingDraft;
      if (isEditRoute) {
        existingDraft = await callApi(draftApi.lockById, {
          onError: (err) => {
            if (isLockError(err)) {
              hasLockErrorRef.current = true;
              history.replace(getSearchPageByProcedure(history));
              dispatch(stopApplicationLoading());
            }
          },
        })(submissionId);
        existingDraft = await postProcess(existingDraft, 'event_migration', undefined);
      } else {
        existingDraft = await callApi(draftApi.getById)(submissionId);
      }
      dispatch(stopApplicationLoading());
      setSubmission(existingDraft);
      userReferenceRef.current = getUserReference(existingDraft);
      submissionRef.current = existingDraft;
    }
  }, [setSubmission, submissionId, dispatch, isEditRoute]);

  // initiate existing draft
  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  // initiate empty draft
  useEffect(() => {
    if (isNewRoute) {
      setSubmission({
        procedure: procedure.id,
        receivingOffice: procedure.receivingOffice,
        initialSubmissionProcedure,
      });
    }
  }, [isNewRoute]);

  // // reload on draftReload event
  // const reloadSubmission = useCallback(async () => {
  //   if (draftState.draftUpdateState === draftUpdateState.DRAFT_RELOAD) {
  //     loadDraft();
  //     dispatchDraftState({ type: draftActions.DRAFT_RELOAD_FINISH });
  //     dispatch(stopApplicationLoading());
  //   }
  //   if (draftState.draftUpdateState === draftUpdateState.DRAFT_RELOAD_WITH_ERROR) {
  //     dispatch(stopApplicationLoading());
  //   }
  // }, [draftState.draftUpdateState, dispatch]);

  const saveForm = useCallback(
    async (sub) => {
      let updatedDraft;
      const sanitizedSubmission = sanitizeSubmission(sub || submission, errorsRef.current);

      if (!submissionId) {
        updatedDraft = await callApi(draftApi.create)({
          ...sanitizedSubmission,
          id: submissionId,
          receivingOffice: procedure.receivingOffice,
        });

        updatedDraft = await postProcess(updatedDraft, 'event_new_draft_created');
        const {
          workflowValidations: newWorkflowValidations,
          uiSchema: newUiSchema,
        } = await handleBusinessRuleValidations(updatedDraft, uiSchema, null, null);
        setWorkflowValidations(newWorkflowValidations);
        setUiSchema(newUiSchema);
        setExtraErrors(newWorkflowValidations.extraErrors);

        history.replace(
          RoutePath.generateRoutePath(RoutePath.PROCEDURE_EDIT, {
            procedure: updatedDraft.procedure,
            submissionId: updatedDraft.id,
          }),
        );
      } else {
        updatedDraft = await postProcess(sanitizedSubmission, 'event_draft_saved');
      }

      setSubmission(updatedDraft);
      submissionRef.current = updatedDraft;

      // TODO: review if this is needed
      // dispatchDraftState({
      //   type: draftActions.SUBMIT,
      // });
    },
    [
      submission,
      setSubmission,
      submissionId,
      dispatch,
      dispatchDraftState,
      history,
    ],
  );

  const onSaveAndClose = useCallback(async () => {
    await saveForm();
    const params = {
      procedure: submission?.procedure,
      ...(submission?.initialSubmissionProcedure && {
        initialSubmissionProcedure: submission?.initialSubmissionProcedure,
      }),
    };
    history.push(RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, params));
  }, [saveForm, history]);

  const onClose = useCallback(async () => {
    history.push('/');
  }, [history]);

  const onSave = useCallback(async () => {
    await saveForm();
    dispatch(showAlertSuccess(createMessage(t('draft.saved.successfully'))));
  }, [saveForm]);

  const sendToSign = useCallback(async () => {
    await callApi(draftApi.update)(submissionId, submission);
    submissionRef.current = await callApi(draftApi.sendToSign)(submissionId, true);
    history.push('/');
  }, [submission, submissionId, dispatch, history]);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, [setIsDialogOpen]);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, [setIsDialogOpen]);

  const confirmDialog = useCallback(() => {
    setIsDialogOpen(false);
    sendToSign();
  }, [setIsDialogOpen, sendToSign]);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen(status => !status);
  }, [setIsDrawerOpen]);

  useEffect(() => {
    const userReferenceSet =
      draftState.submissionUpdateState === submissionUpdateState.USER_REFERENCE_SET &&
      submission.basicFilingInfo?.userReference;

    if (userReferenceSet) {
      if (!submission.status) {
        saveForm(submission);
        loadDraft();
      }
      dispatchDraftState({ type: submissionUpdateState.USER_REFERENCE_UNSET });
      dispatchDraftState({ type: draftActions.CALCULATE_WORKFLOW_PROGRESS_START });
    }
  }, [draftState.submissionUpdateState, loadDraft, submission]);

  // useEffect(() => {
  //   reloadSubmission();
  // }, [draftState.draftUpdateState, reloadSubmission]);

  return {
    procedure,
    submission,
    setSubmission,
    submissionId,
    validationSchema,
    uiSchema,
    setUiSchema,
    workflowValidations,
    setWorkflowValidations,
    extraErrors,
    setExtraErrors,
    dispatchDraftState,
    onSave,
    onSaveAndClose,
    onClose,
    saveForm,
    openDialog,
    closeDialog,
    confirmDialog,
    isDialogOpen,
    isDrawerOpen,
    toggleDrawer,
    errors,
    initialSubmissionProcedure: initialSubmissionProcedure || submission?.initialSubmissionProcedure,
    setErrors: setErrorsWrapper,
    isVersionCorrect,
  };
}
